import {
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Stack,
  Autocomplete,
  Chip
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { NewTask } from "./types";
import { useOrderInfo } from "../../shared/orderInfo/OrderInfoContext";
import { Contact } from "../../shared/orderInfo/types";

const AddTaskModal = ({
  open,
  closeModal,
  applicationId,
  orderNumber,
  refreshTasks,
  tasks
}: {
  open: boolean;
  closeModal: () => void;
  applicationId: number;
  orderNumber: string | null;
  refreshTasks: () => Promise<void>;
  tasks: NewTask[];
}) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [description, setDescription] = useState<string>("");
  const [selectedParentTask, setSelectedParent] = useState<NewTask | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const orderInfo = useOrderInfo();

  const excludedContactTypes = [
    "Abstractor",
    "Appraiser",
    "GeneralContractor",
    "Government",
    "HOA",
    "HomeInspector",
    "Surveyor",
    "Underwriter",
    "SettlementAgent"
  ];

  const contacts = orderInfo?.contacts.filter((c) => c.name && !excludedContactTypes.includes(c.type)) || [];

  const save = async () => {
    setSubmitting(true);
    await axios.post("/api/taskmanagement/newtasks/addNewTask", {
      newTask: {
        applicationId,
        orderNumber,
        description,
        parentTaskTag: selectedParentTask?.description,
        contactCode: selectedContact?.code
      },
      parentId: selectedParentTask?.id
    });
    refreshTasks();
    closeModal();
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="subtitle1" marginBottom={3}>
          New Task
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 3 }}>
        <Box paddingTop={1}>
          <Stack direction="column" spacing={2}>
            <Autocomplete
              options={tasks}
              renderInput={(params) => <TextField {...params} label="Parent Task (optional)" />}
              onChange={(_, val) => setSelectedParent(val)}
              getOptionLabel={(option) => option.description}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}
                >
                  {!option.parentTaskTag && <Typography variant="subtitle2">{option.description}</Typography>}
                  {option.parentTaskTag && (
                    <Typography variant="subtitle2" display="inline-block" marginBottom={0.5}>
                      {option.parentTaskTag && (
                        <Chip
                          variant="soft"
                          size="small"
                          label={option.parentTaskTag}
                          sx={{
                            borderRadius: 1,
                            fontWeight: 600,
                            marginRight: 1
                          }}
                        />
                      )}
                      {option.description}
                    </Typography>
                  )}
                </Box>
              )}
            />
            <TextField
              fullWidth
              label="Task Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <Autocomplete
              renderInput={(params) => (
                <TextField {...params} placeholder="Responsible Party" size="small" variant="outlined" />
              )}
              getOptionLabel={(o) => {
                if (o.type === "Attorney" && o.represents) {
                  let { represents } = o;
                  if (represents.charAt(represents.length - 1) !== "s") {
                    represents += "s";
                  }
                  return `${represents} Attorney - ${o.name}`;
                }
                return `${o.code} - ${o.name || ""}`;
              }}
              options={contacts}
              value={selectedContact}
              onChange={(_, value) => setSelectedContact(value)}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={closeModal} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" disabled={submitting} onClick={save}>
          {submitting ? "Saving...." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTaskModal;
